import React, { FC, useEffect, useMemo, useState } from 'react'
import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'

import type { ReportCargoDirection } from '../../../../redux/types/reportGroupedCargos'
import type { PickupSenderDef, ShipmentRow } from './cargoTypes'
import PickupSenderCard, {
  PICKUP_SENDER_DRAG_DELAY_MS,
  PICKUP_SENDER_MOBILE_MQ,
} from './PickupSenderCard'

type Props = {
  reportCargoDirection: ReportCargoDirection
  readOnly?: boolean
  /** Ручная сортировка карточек отправителей/получателей. По умолчанию true. */
  canReorder?: boolean
  senders: PickupSenderDef[]
  onReorder: (next: PickupSenderDef[]) => void
  onDragEndPersist?: (next: PickupSenderDef[]) => void
  onRequestDeleteDelivery: (senderRowKey: string) => void
  onRequestDeleteShipment: (row: ShipmentRow) => void
  onRequestEditShipment: (row: ShipmentRow) => void
  onRequestAddShipment: (sender: PickupSenderDef) => void
  openListIds: string[]
  togglePickupList: (id: string) => void
  pickupChecked: Record<string, boolean>
  pendingBySender?: Record<string, boolean>
  canAddShipment?: boolean
  checklistMode: 'pickup' | 'delivered'
  hideDeliveryDelete?: boolean
  setSenderAndShipments: (
    mode: 'pickup' | 'delivered',
    senderKey: string,
    shipmentIds: string[],
    checked: boolean
  ) => void
  toggleShipmentOnly: (
    mode: 'pickup' | 'delivered',
    id: string,
    checked: boolean,
    parentSenderKey?: string,
    siblingShipmentIds?: string[]
  ) => void
  openPhoneKey: string | null
  setOpenPhoneKey: (k: string | null) => void
}

type CardSharedProps = {
  reportCargoDirection: ReportCargoDirection
  sender: PickupSenderDef
  listOpen: boolean
  togglePickupList: (id: string) => void
  pickupChecked: Record<string, boolean>
  pendingBySender?: Props['pendingBySender']
  setSenderAndShipments: Props['setSenderAndShipments']
  toggleShipmentOnly: Props['toggleShipmentOnly']
  openPhoneKey: string | null
  setOpenPhoneKey: Props['setOpenPhoneKey']
  onRequestDeleteDelivery: Props['onRequestDeleteDelivery']
  onRequestDeleteShipment: Props['onRequestDeleteShipment']
  onRequestEditShipment: Props['onRequestEditShipment']
  onRequestAddShipment: Props['onRequestAddShipment']
  canAddShipment?: Props['canAddShipment']
  checklistMode: Props['checklistMode']
  hideDeliveryDelete?: Props['hideDeliveryDelete']
  readOnly?: Props['readOnly']
}

const PickupSenderCardShared: FC<
  CardSharedProps & {
    variant: 'sortable' | 'static'
    sortable?: React.ComponentProps<typeof PickupSenderCard>['sortable']
  }
> = ({
  reportCargoDirection,
  sender,
  listOpen,
  togglePickupList,
  pickupChecked,
  pendingBySender,
  setSenderAndShipments,
  toggleShipmentOnly,
  openPhoneKey,
  setOpenPhoneKey,
  onRequestDeleteDelivery,
  onRequestDeleteShipment,
  onRequestEditShipment,
  onRequestAddShipment,
  canAddShipment,
  checklistMode,
  hideDeliveryDelete,
  readOnly,
  variant,
  sortable,
}) => (
  <PickupSenderCard
    reportCargoDirection={reportCargoDirection}
    variant={variant}
    sortable={sortable}
    sender={sender}
    listOpen={listOpen}
    togglePickupList={togglePickupList}
    pickupChecked={pickupChecked}
    checkboxDisabled={
      Boolean(readOnly) || Boolean(pendingBySender?.[sender.rowKey])
    }
    setSenderAndShipments={setSenderAndShipments}
    toggleShipmentOnly={toggleShipmentOnly}
    openPhoneKey={openPhoneKey}
    setOpenPhoneKey={setOpenPhoneKey}
    onRequestDeleteDelivery={
      readOnly ? undefined : () => onRequestDeleteDelivery(sender.rowKey)
    }
    onRequestDeleteShipment={readOnly ? undefined : onRequestDeleteShipment}
    onRequestEditShipment={readOnly ? undefined : onRequestEditShipment}
    onRequestAddShipment={readOnly ? undefined : onRequestAddShipment}
    canAddShipment={canAddShipment}
    checklistMode={checklistMode}
    hideDeliveryDelete={hideDeliveryDelete}
  />
)

const SortablePickupSenderCard: FC<CardSharedProps> = (props) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: props.sender.rowKey })

  const style: React.CSSProperties = {
    transform: transform
      ? `translate3d(${transform.x}px, ${transform.y}px, 0)`
      : undefined,
    transition,
    position: 'relative',
    zIndex: isDragging ? 2 : undefined,
  }

  return (
    <PickupSenderCardShared
      {...props}
      variant="sortable"
      sortable={{
        setNodeRef,
        style,
        attributes,
        dragHandleListeners: listeners,
        isDragging,
      }}
    />
  )
}

const PickupSendersSortable: FC<Props> = ({
  reportCargoDirection,
  readOnly,
  canReorder = true,
  senders,
  onReorder,
  onDragEndPersist,
  onRequestDeleteDelivery,
  onRequestDeleteShipment,
  onRequestEditShipment,
  onRequestAddShipment,
  openListIds,
  togglePickupList,
  pickupChecked,
  pendingBySender,
  canAddShipment,
  checklistMode,
  hideDeliveryDelete,
  setSenderAndShipments,
  toggleShipmentOnly,
  openPhoneKey,
  setOpenPhoneKey,
}) => {
  const [isMobileViewport, setIsMobileViewport] = useState(false)
  const allowReorder = canReorder && !readOnly

  useEffect(() => {
    const mq = window.matchMedia(PICKUP_SENDER_MOBILE_MQ)
    const apply = () => setIsMobileViewport(mq.matches)
    apply()
    mq.addEventListener('change', apply)
    return () => mq.removeEventListener('change', apply)
  }, [])

  const pointerActivation = useMemo(
    () =>
      isMobileViewport
        ? { delay: PICKUP_SENDER_DRAG_DELAY_MS, tolerance: 5 }
        : { distance: 8 },
    [isMobileViewport]
  )

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: pointerActivation,
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  const handleDragEnd = (event: DragEndEvent) => {
    if (!allowReorder) return
    const { active, over } = event
    if (!over || active.id === over.id) return
    const oldIndex = senders.findIndex((s) => s.rowKey === active.id)
    const newIndex = senders.findIndex((s) => s.rowKey === over.id)
    if (oldIndex < 0 || newIndex < 0) return
    const next = arrayMove(senders, oldIndex, newIndex)
    onReorder(next)
    onDragEndPersist?.(next)
  }

  const cardProps = (sender: PickupSenderDef): CardSharedProps => ({
    reportCargoDirection,
    sender,
    listOpen: !!sender.listId && openListIds.includes(sender.listId),
    togglePickupList,
    pickupChecked,
    pendingBySender,
    setSenderAndShipments,
    toggleShipmentOnly,
    openPhoneKey,
    setOpenPhoneKey,
    onRequestDeleteDelivery,
    onRequestDeleteShipment,
    onRequestEditShipment,
    onRequestAddShipment,
    canAddShipment,
    checklistMode,
    hideDeliveryDelete,
    readOnly,
  })

  if (!allowReorder) {
    return (
      <>
        {senders.map((sender) => (
          <PickupSenderCardShared
            key={sender.rowKey}
            {...cardProps(sender)}
            variant="static"
          />
        ))}
      </>
    )
  }

  const ids = senders.map((s) => s.rowKey)

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        {senders.map((sender) => (
          <SortablePickupSenderCard
            key={sender.rowKey}
            {...cardProps(sender)}
          />
        ))}
      </SortableContext>
    </DndContext>
  )
}

export default PickupSendersSortable
